import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { programsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ListChecks, CalendarClock, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  blocked: 'bg-red-50 text-red-700 border-red-200',
};
const PRIORITY_COLORS = { low: 'text-slate-500', medium: 'text-amber-600', high: 'text-red-600' };

export default function MyTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showDelayModal, setShowDelayModal] = useState(null);
  const [delayForm, setDelayForm] = useState({ new_due_date: '', delay_reason: '' });

  const loadTasks = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await programsAPI.myTasks(params);
      setTasks(res.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTasks(); }, [filterStatus]);

  const handleStatusChange = async (task, newStatus) => {
    try {
      await programsAPI.updateTask(task.program_id, task.id, { status: newStatus });
      toast.success('Status updated');
      loadTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelaySubmit = async () => {
    if (!delayForm.new_due_date || !delayForm.delay_reason) {
      toast.error('Both new date and reason are required');
      return;
    }
    const task = showDelayModal;
    try {
      await programsAPI.updateTask(task.program_id, task.id, {
        due_date: delayForm.new_due_date,
        delay_reason: delayForm.delay_reason,
      });
      toast.success('Due date updated');
      setShowDelayModal(null);
      setDelayForm({ new_due_date: '', delay_reason: '' });
      loadTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const overdue = tasks.filter(t => t.due_date && t.due_date < todayStr && t.status !== 'completed');
  const upcoming = tasks.filter(t => t.status !== 'completed' && (!t.due_date || t.due_date >= todayStr));
  const completed = tasks.filter(t => t.status === 'completed');

  return (
    <div className="space-y-4" data-testid="my-tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><ListChecks className="w-5 h-5" /> My Tasks</h1>
          <p className="text-xs text-muted-foreground mt-0.5">All tasks assigned to you across programs</p>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="py-3 px-4"><p className="text-2xl font-bold">{tasks.length}</p><p className="text-xs text-muted-foreground">Total Tasks</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="py-3 px-4"><p className="text-2xl font-bold text-red-600">{overdue.length}</p><p className="text-xs text-muted-foreground">Overdue</p></CardContent></Card>
        <Card><CardContent className="py-3 px-4"><p className="text-2xl font-bold text-blue-600">{upcoming.length}</p><p className="text-xs text-muted-foreground">Active/Upcoming</p></CardContent></Card>
        <Card><CardContent className="py-3 px-4"><p className="text-2xl font-bold text-emerald-600">{completed.length}</p><p className="text-xs text-muted-foreground">Completed</p></CardContent></Card>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Loading tasks...</p>
      ) : tasks.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No tasks assigned to you</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {/* Overdue */}
          {overdue.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-red-700 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Overdue ({overdue.length})</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {overdue.map(task => <TaskRow key={task.id} task={task} navigate={navigate} onStatusChange={handleStatusChange} onChangeDate={t => { setShowDelayModal(t); setDelayForm({ new_due_date: '', delay_reason: '' }); }} />)}
              </CardContent>
            </Card>
          )}

          {/* Active */}
          {upcoming.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Active / Upcoming ({upcoming.length})</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {upcoming.map(task => <TaskRow key={task.id} task={task} navigate={navigate} onStatusChange={handleStatusChange} onChangeDate={t => { setShowDelayModal(t); setDelayForm({ new_due_date: '', delay_reason: '' }); }} />)}
              </CardContent>
            </Card>
          )}

          {/* Completed */}
          {completed.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-emerald-700">Completed ({completed.length})</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {completed.map(task => <TaskRow key={task.id} task={task} navigate={navigate} onStatusChange={handleStatusChange} />)}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Delay Modal */}
      <Dialog open={!!showDelayModal} onOpenChange={() => setShowDelayModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-600" /> Change Due Date</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {showDelayModal && (
              <p className="text-xs text-muted-foreground">Task: <strong>{showDelayModal.title}</strong> — Current due: {showDelayModal.due_date}</p>
            )}
            <div>
              <Label className="text-xs">New Due Date *</Label>
              <Input type="date" className="h-8 text-xs mt-1" value={delayForm.new_due_date}
                onChange={e => setDelayForm(f => ({ ...f, new_due_date: e.target.value }))}
                data-testid="my-tasks-delay-date" />
            </div>
            <div>
              <Label className="text-xs">Reason for Delay *</Label>
              <Textarea className="text-xs mt-1" rows={3} value={delayForm.delay_reason}
                onChange={e => setDelayForm(f => ({ ...f, delay_reason: e.target.value }))}
                placeholder="Explain the reason for delay..."
                data-testid="my-tasks-delay-reason" />
            </div>
            <Button size="sm" className="w-full" onClick={handleDelaySubmit} data-testid="my-tasks-delay-submit">Update Due Date</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskRow({ task, navigate, onStatusChange, onChangeDate }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const isOverdue = task.due_date && task.due_date < todayStr && task.status !== 'completed';

  return (
    <div className={`p-2.5 rounded border ${isOverdue ? 'bg-red-50/50 border-red-200' : 'bg-muted/30'}`} data-testid={`my-task-${task.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[task.status] || ''}`}>{task.status}</Badge>
          <span className={`text-[10px] font-semibold shrink-0 ${PRIORITY_COLORS[task.priority] || ''}`}>{task.priority?.toUpperCase()}</span>
          <p className="text-xs font-medium truncate">{task.title}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Select value={task.status} onValueChange={v => onStatusChange(task, v)}>
            <SelectTrigger className="h-7 w-28 text-[10px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
          {onChangeDate && task.due_date && task.status !== 'completed' && (
            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-amber-600" onClick={() => onChangeDate(task)}>
              <CalendarClock className="w-3 h-3 mr-0.5" /> Date
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => navigate(`/programs/${task.program_id}`)}>
            <ExternalLink className="w-3 h-3 mr-0.5" /> View
          </Button>
        </div>
      </div>
      <div className="mt-1 text-[10px] text-muted-foreground flex flex-wrap gap-x-3">
        <span>{task.program_name} ({task.program_number})</span>
        {task.customer_name && <span>{task.customer_name}</span>}
        {task.due_date && <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>Due: {task.due_date}</span>}
        {task.is_delayed && <span className="text-amber-600">Delayed x{task.delay_history?.length || 0}</span>}
      </div>
    </div>
  );
}
